import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FileText, ArrowLeft } from 'lucide-react'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import { sanitizeHtml } from '../utils/sanitizeHtml'

interface Announcement {
  id: number
  title: string
  content: string
  content_type: string
  is_active: boolean
  created_time: string
  modified_time: string
}

export default function SubscriptionTerms() {
  const [terms, setTerms] = useState<Announcement | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchTerms()
  }, [])

  const fetchTerms = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await publicAPI.getAnnouncements({
        content_type: 'subscription_terms',
      })

      const activeTerms = response.data.data.find(
        (announcement: Announcement) => announcement.is_active === true
      )

      if (activeTerms) {
        setTerms(activeTerms)
      } else {
        setError('Ingen aktive abonnementsvilkår funnet.')
      }
    } catch (err: any) {
      console.error('Failed to fetch subscription terms:', err)
      setError('Kunne ikke laste abonnementsvilkår. Vennligst prøv igjen senere.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-linear-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8 text-center">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-linear-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <ErrorMessage message={error} />
          <div className="text-center mt-4">
            <Link to="/subscribe" className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400">
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Tilbake til abonnement
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (!terms) {
    return (
      <div className="min-h-screen bg-linear-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
            Ingen abonnementsvilkår tilgjengelig.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {terms.title}
            </h1>
          </div>

          <div
            className="prose dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(terms.content) }}
          />

          {terms.modified_time && (
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Sist oppdatert: {new Date(terms.modified_time).toLocaleDateString('nb-NO', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          )}

          <div className="mt-6">
            <Link to="/subscribe" className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400">
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Tilbake til abonnement
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
